import type { CSSProperties, JSX, ReactNode } from 'react';

/**
 * Table shell.
 *
 * The wrapper owns `overflow-x: auto`, so a wide table scrolls inside its own
 * box and the page body never scrolls sideways.
 */
export function TableWrap({
  children,
  scrollY = false,
  style,
}: {
  readonly children: ReactNode;
  /** Caps the height and scrolls vertically, for long finding lists. */
  readonly scrollY?: boolean | undefined;
  readonly style?: CSSProperties | undefined;
}): JSX.Element {
  return (
    <div className={`tbl-wrap ${scrollY ? 'scroll-y' : ''}`} style={style}>
      {children}
    </div>
  );
}

export function Table({
  children,
  caption,
}: {
  readonly children: ReactNode;
  /** Screen-reader description of what the table holds. */
  readonly caption?: string | undefined;
}): JSX.Element {
  return (
    <table className="tbl">
      {caption !== undefined && <caption className="sr-only">{caption}</caption>}
      {children}
    </table>
  );
}

export type SortDirection = 1 | -1;

/** Sortable header cell. Announces direction via aria-sort. */
export function SortableTh({
  label,
  sortKey,
  activeKey,
  direction,
  onSort,
  numeric = false,
}: {
  readonly label: string;
  readonly sortKey: string;
  readonly activeKey: string;
  readonly direction: SortDirection;
  readonly onSort: (key: string) => void;
  readonly numeric?: boolean | undefined;
}): JSX.Element {
  const active = activeKey === sortKey;
  return (
    <th
      className={`sortable ${numeric ? 'num' : ''}`}
      aria-sort={active ? (direction > 0 ? 'ascending' : 'descending') : 'none'}
      onClick={() => {
        onSort(sortKey);
      }}
    >
      {label}
      {active ? (direction > 0 ? ' ▲' : ' ▼') : ''}
    </th>
  );
}

/** Skeleton rows for a pending table. */
export function SkeletonRows({
  rows = 4,
  cols = 4,
}: {
  readonly rows?: number | undefined;
  readonly cols?: number | undefined;
}): JSX.Element {
  return (
    <tbody>
      {Array.from({ length: rows }, (_, r) => (
        <tr key={r}>
          {Array.from({ length: cols }, (_, c) => (
            <td key={c}>
              <div className="skel" />
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  );
}
